import React from 'react';
import { PropertyGroup } from '../PropertyGroup';
import { PropertyNumber } from '../property-types/PropertyNumber';
import { PropertyColor } from '../property-types/PropertyColor';
import { PropertySelect } from '../property-types/PropertySelect';
import { PropertyTextArea } from '../property-types/PropertyTextArea';
import { AlignLeft, AlignCenter, AlignRight } from 'lucide-react';

interface TextData {
  content: string;
  fontSize: number;
  fontFamily: string;
  fontWeight: string;
  color: string;
  textAlign: string;
}

interface TextGroupProps {
  text: TextData;
  onTextChange: (property: keyof TextData, value: string | number) => void;
}

const FONT_FAMILIES = [
  { value: 'Arial, sans-serif', label: 'Arial' },
  { value: 'Helvetica, sans-serif', label: 'Helvetica' },
  { value: 'Times New Roman, serif', label: 'Times New Roman' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: 'Courier New, monospace', label: 'Courier New' },
  { value: 'Verdana, sans-serif', label: 'Verdana' },
  { value: 'system-ui, sans-serif', label: 'System' },
];

const FONT_WEIGHTS = [
  { value: '300', label: 'Light' },
  { value: '400', label: 'Normal' },
  { value: '500', label: 'Medium' },
  { value: '600', label: 'Semi Bold' },
  { value: '700', label: 'Bold' },
  { value: '800', label: 'Extra Bold' },
];

export const TextGroup: React.FC<TextGroupProps> = ({
  text,
  onTextChange
}) => {
  return (
    <PropertyGroup title="Text">
      {/* Text Content */}
      <div className="mt-1">
        <PropertyTextArea
          label="Content"
          value={text.content}
          onChange={(value) => onTextChange('content', value)}
          rows={3}
        />
      </div>

      {/* Font Size */}
      <div className="flex items-center gap-2 py-0.5 mt-1">
        <span className="text-gray-900 dark:text-white text-sm font-medium w-[30%] flex-shrink-0">Size</span>
        <div className="flex flex-1 gap-2">
          <PropertyNumber
            label=""
            value={text.fontSize}
            onChange={(value) => onTextChange('fontSize', value)}
            step={1}
            min={8}
            max={200}
            suffix="px"
          />
        </div>
      </div>

      {/* Font Family */}
      <div className="flex items-center gap-2 py-0.5">
        <span className="text-gray-900 dark:text-white text-sm font-medium w-[30%] flex-shrink-0">Font</span>
        <div className="flex flex-1 gap-2">
          <PropertySelect
            label=""
            value={text.fontFamily}
            options={FONT_FAMILIES}
            onChange={(value) => onTextChange('fontFamily', value)}
          />
        </div>
      </div>

      {/* Font Weight */}
      <div className="flex items-center gap-2 py-0.5">
        <span className="text-gray-900 dark:text-white text-sm font-medium w-[30%] flex-shrink-0">Weight</span>
        <div className="flex flex-1 gap-2">
          <PropertySelect
            label=""
            value={text.fontWeight}
            options={FONT_WEIGHTS}
            onChange={(value) => onTextChange('fontWeight', value)}
          />
        </div>
      </div>

      {/* Color */}
      <div className="flex items-center gap-2 py-0.5">
        <span className="text-gray-900 dark:text-white text-sm font-medium w-[30%] flex-shrink-0">Color</span>
        <div className="flex flex-1 gap-2">
          <PropertyColor
            label=""
            value={text.color}
            onChange={(value) => onTextChange('color', value)}
          />
        </div>
      </div>

      {/* Text Align */}
      <div className="flex items-center gap-2 py-0.5">
        <span className="text-gray-900 dark:text-white text-sm font-medium w-[30%] flex-shrink-0">Align</span>
        <div className="flex flex-1 gap-1">
          <button
            onClick={() => onTextChange('textAlign', 'left')}
            className={`flex-1 p-2 rounded transition-colors ${
              text.textAlign === 'left'
                ? 'bg-primary-500 text-white'
                : 'bg-gray-900 text-gray-400 hover:bg-gray-700'
            }`}
          >
            <AlignLeft className="w-4 h-4 mx-auto" />
          </button>
          <button
            onClick={() => onTextChange('textAlign', 'center')}
            className={`flex-1 p-2 rounded transition-colors ${
              text.textAlign === 'center'
                ? 'bg-primary-500 text-white'
                : 'bg-gray-900 text-gray-400 hover:bg-gray-700'
            }`}
          >
            <AlignCenter className="w-4 h-4 mx-auto" />
          </button>
          <button
            onClick={() => onTextChange('textAlign', 'right')}
            className={`flex-1 p-2 rounded transition-colors ${
              text.textAlign === 'right'
                ? 'bg-primary-500 text-white'
                : 'bg-gray-900 text-gray-400 hover:bg-gray-700'
            }`}
          >
            <AlignRight className="w-4 h-4 mx-auto" />
          </button>
        </div>
      </div>
    </PropertyGroup>
  );
};

